import { fetchAuthSession } from 'aws-amplify/auth';
import type { ApiError } from '@/types/api';
import { useAuthStore } from '@/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.dev.socialyse.ai/v1';

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public error?: ApiError
  ) {
    super(message);
    this.name = 'ApiClientError';
  }

  /** Machine-readable error code from the backend (e.g. RATE_LIMITED, INVALID_PLATFORM) */
  get code(): string | undefined {
    return this.error?.error;
  }
}

/**
 * Get authentication token from Amplify Cognito session
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Base API client with auth interceptor
 */
export async function apiClient<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs = 15_000
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  // Get auth token if available
  const token = await getAuthToken();

  // Merge headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // Add auth header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      signal: AbortSignal.timeout(timeoutMs), // default 15 s – prevents infinite spinner
    });

    // Handle non-OK responses
    if (!response.ok) {
      let error: ApiError | undefined;

      try {
        error = await response.json();
      } catch {
        // Response body is not JSON
      }

      // Handle 401 Unauthorized - trigger session expiry flow
      if (response.status === 401) {
        const { setSessionExpired, signOut } = useAuthStore.getState();
        signOut();
        setSessionExpired(true);
      }

      throw new ApiClientError(
        error?.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        error
      );
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    // Parse JSON response
    return await response.json();
  } catch (error) {
    // Re-throw ApiClientError as-is
    if (error instanceof ApiClientError) {
      throw error;
    }

    // Network error or other fetch errors
    throw new ApiClientError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

/**
 * API client for GET requests
 */
export async function apiGet<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return apiClient<T>(endpoint, {
    ...options,
    method: 'GET',
  });
}

/**
 * API client for POST requests
 */
export async function apiPost<T>(
  endpoint: string,
  body?: unknown,
  options?: RequestInit,
  timeoutMs?: number
): Promise<T> {
  return apiClient<T>(
    endpoint,
    {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    },
    timeoutMs
  );
}

/**
 * API client for PUT requests
 */
export async function apiPut<T>(
  endpoint: string,
  body?: unknown,
  options?: RequestInit
): Promise<T> {
  return apiClient<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * API client for DELETE requests
 */
export async function apiDelete<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return apiClient<T>(endpoint, {
    ...options,
    method: 'DELETE',
  });
}
